import { buttonBaseClasses } from '@mui/material';
import Box, { BoxProps } from '@mui/material/Box';
import Button, { ButtonProps } from '@mui/material/Button';
import CircularProgress, { CircularProgressProps } from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import Typography, { TypographyProps } from '@mui/material/Typography';
import { SxProps, Theme } from '@mui/system';
import { forwardRef } from 'react';

import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';

import { orderDispatchColumns, OrdersDispatchColumnKey } from './ordersDispatchColumns';

type LoadingSpinnerProps = {
  isVisible?: boolean;
  loadingIndicatorColor?: CircularProgressProps['color'];
  loadingIndicatorSize?: number;
  sx?: SxProps<Theme>;
};

export function LoadingSpinner({
  isVisible,
  loadingIndicatorColor,
  loadingIndicatorSize = 30,
  sx,
}: LoadingSpinnerProps) {
  return (
    <Box
      data-test-id="loading-spinner"
      sx={[
        {
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
        {
          display: isVisible ? 'flex' : 'none',
          visibility: isVisible ? 'visible' : 'hidden',
        },
      ]}
    >
      <CircularProgress size={loadingIndicatorSize} color={loadingIndicatorColor} />
    </Box>
  );
}

type HeaderPanelProps = BoxProps;
export const HEADER_PANEL_Z_INDEX = 100;

const StyledHeaderPanel = styled(Box)<HeaderPanelProps>(({ theme }) => {
  return {
    alignContent: 'center',
    backgroundColor: theme.semantic.surfaceRaised,
    borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
  };
});

export function HeaderPanel(props: HeaderPanelProps) {
  return <StyledHeaderPanel {...props} />;
}

type OverflowAwareTextProps = TypographyProps;

const StyledOverflowAwareText = styled(Typography)<OverflowAwareTextProps>(({
  theme,
}) => {
  return {
    color: theme.brandV2.colors.treadBlack,
    fontSize: '12px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  };
});

export const OverflowAwareText = forwardRef<HTMLSpanElement, OverflowAwareTextProps>(
  (props, ref) => {
    return <StyledOverflowAwareText ref={ref} {...props} />;
  },
);

OverflowAwareText.displayName = 'OverflowAwareText';

type ColumnProps = BoxProps & {
  columnKey: OrdersDispatchColumnKey;
  isHeader?: boolean;
};

const StyledColumn = styled(Box, {
  shouldForwardProp: (propName) => propName !== 'isHeader',
})<Omit<ColumnProps, 'columnKey'>>(({ isHeader, theme }) => {
  return {
    overflow: 'hidden',
    padding: theme.spacing(0, 1),
    ...(isHeader ? { display: 'flex', alignItems: 'center' } : {}),
  };
});

export function Column({ columnKey, ...rest }: ColumnProps) {
  return <StyledColumn sx={orderDispatchColumns[columnKey].sx} {...rest} />;
}

type FlexColumnProps = BoxProps;

const StyledFlexColumn = styled(Box)<FlexColumnProps>(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  };
});

export function FlexColumn(props: FlexColumnProps) {
  return <StyledFlexColumn {...props} />;
}

const smallButtonCommonStyles = (theme: Theme) => ({
  [`&.${buttonBaseClasses.root}`]: {
    boxShadow: 'none',
    fontSize: '12px',
    fontWeight: 600,
    height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
    minWidth: 'auto',
    padding: `0 ${theme.spacing(1.5)}`,
    whiteSpace: 'nowrap',
  },
});

const StyledLoadingButton = styled(Button)<ButtonProps>(({ theme }) => {
  return { ...smallButtonCommonStyles(theme) };
});

export function SmallLoadingButton(props: ButtonProps) {
  return <StyledLoadingButton {...props} />;
}

const StyledButton = styled(Button)(({ theme }) => {
  return { ...smallButtonCommonStyles(theme) };
});

export const SmallButton = StyledButton as typeof Button;

// SmallButton reserves horizontal padding for a text label these icon-only controls don't
// have. The selector matches SmallButton's own so it wins on specificity.
export const squareIconButtonSx = {
  [`&.${buttonBaseClasses.root}`]: {
    minWidth: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
    padding: 0,
    width: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
  },
};
